/**
 * Maps a backend DotNet type name to a frontend value type.
 * Nullable value types (e.g. "System.Nullable`1[System.Boolean]" or its assembly-qualified
 * form) are mapped like their underlying type, since nullability is transported separately.
 * @param dotNetType - The full DotNet type name.
 * @returns The frontend value type.
 */
export function mapDotNetValueType(dotNetType: string): 'string' | 'number' | 'boolean' {
  const underlying = /^System\.Nullable`1\[\[?([^,\]]+)/.exec(dotNetType ?? '')?.[1] ?? dotNetType ?? '';
  const normalized = underlying.trim().split('.').pop()?.toLowerCase();
  switch (normalized) {
    case 'boolean':
      return 'boolean';
    case 'int16':
    case 'int32':
    case 'int64':
    case 'double':
    case 'decimal':
    case 'single':
    case 'float':
    case 'byte':
      return 'number';
    default:
      return 'string';
  }
}

export enum DotNetType {
  String = 'String', // System.String
  Number = 'Number', // Int32, Int64, Double, Decimal...
  Boolean = 'Boolean', // System.Boolean
  Enum = 'Enum',
  Array = 'Array',
  Dictionary = 'Dictionary`2',
  Object = 'Object'
}

export type MapDotNetType<T extends DotNetType> = T extends DotNetType.String
  ? string
  : T extends DotNetType.Number
    ? number
    : T extends DotNetType.Boolean
      ? boolean
      : T extends DotNetType.Enum
        ? number
        : T extends DotNetType.Array
          ? any[]
          : T extends DotNetType.Dictionary
            ? Record<string, any>
            : T extends DotNetType.Object
              ? Record<string, any>
              : never;
